export interface RankingItem {
  rank: number;
  filmId: string;
  score: number;
  trend: "up" | "down" | "flat" | "new";
  change: string;
}

export interface CollectionItem {
  id: string;
  name: string;
  count: number;
  desc: string;
  highlights: string[];
  cover: string;
}

export interface EditorPick {
  filmId: string;
  note: string;
  label: string;
}

export interface NewArrival {
  filmId: string;
  date: string;
  subtitle: string;
  format: string;
}

export interface GuideStep {
  id: string;
  title: string;
  summary: string;
  detail: string;
}

export interface FaqItem {
  id: string;
  question: string;
  answer: string;
}

export const highlights = [
  {
    icon: "ri-filter-3-line",
    title: "题材清晰，一屏选片",
    desc: "按家庭伦理、社会现实、悬疑伦理、都市情感与文艺剧情分类，先想清楚今晚想看什么，再决定看哪一部。",
  },
  {
    icon: "ri-movie-2-line",
    title: "中字高清，观看更省心",
    desc: "本站推荐的条目均标注字幕与画质情况，尽量挑选支持高清中字的版本，减少来回找资源的麻烦。",
  },
  {
    icon: "ri-bar-chart-2-line",
    title: "榜单与合集双线推荐",
    desc: "既有实时更新的口碑排行榜，也有按主题打包的合集，想按热度挑或按主题挑都能找到入口。",
  },
  {
    icon: "ri-compass-3-line",
    title: "面向新观众的选片引导",
    desc: "如果你刚接触这个题材，可以先看观影指南里的四步选片法，再配合编辑手记，避免踩到不合适的片子。",
  },
];

export const siteStats = [
  { value: "1,280+", label: "收录条目" },
  { value: "18", label: "本期精选推荐" },
  { value: "6", label: "主题合集" },
  { value: "每日更新", label: "榜单频率" },
];

export const ranking: RankingItem[] = [
  { rank: 1, filmId: "f05", score: 8.9, trend: "flat", change: "持平" },
  { rank: 2, filmId: "f18", score: 8.9, trend: "up", change: "上升 3" },
  { rank: 3, filmId: "f09", score: 8.8, trend: "up", change: "上升 1" },
  { rank: 4, filmId: "f01", score: 8.7, trend: "down", change: "下降 2" },
  { rank: 5, filmId: "f11", score: 8.7, trend: "new", change: "新上榜" },
  { rank: 6, filmId: "f08", score: 8.6, trend: "down", change: "下降 1" },
  { rank: 7, filmId: "f17", score: 8.6, trend: "up", change: "上升 4" },
  { rank: 8, filmId: "f02", score: 8.5, trend: "flat", change: "持平" },
  { rank: 9, filmId: "f13", score: 8.5, trend: "down", change: "下降 3" },
  { rank: 10, filmId: "f04", score: 8.4, trend: "new", change: "新上榜" },
];

export const collections: CollectionItem[] = [
  {
    id: "c1",
    name: "家庭伦理精选合集",
    count: 32,
    desc: "聚焦父母、子女、伴侣之间那些说不清又放不下的关系，适合想安静看一部、看完还能回味很久的观众。",
    highlights: ["母女与父子关系", "再婚与代际冲突", "日常细节里的和解"],
    cover: "https://readdy.ai/api/search-image?query=a%20warm%20quiet%20family%20dining%20table%20with%20empty%20chairs%20and%20afternoon%20light%20through%20curtains%20cinematic%20film%20still%2C%20warm%20amber%20and%20teal%20color%20grading%2C%20soft%20rim%20light%2C%20blurred%20atmospheric%20background%2C%20shallow%20depth%20of%20field%2C%20editorial%20photography%2C%20no%20text%2C%20no%20watermark&width=800&height=500&seq=ethilmic-col-01&orientation=landscape",
  },
  {
    id: "c2",
    name: "社会现实主题合集",
    count: 28,
    desc: "把镜头对准普通人被时代推着走的瞬间，工厂、边境、小镇与车站，是这些故事共同的舞台。",
    highlights: ["劳工与尊严", "迁徙与异乡", "小人物群像"],
    cover: "https://readdy.ai/api/search-image?query=an%20industrial%20town%20street%20at%20dusk%20with%20faded%20factory%20smokestacks%20and%20warm%20window%20lights%20cinematic%20film%20still%2C%20warm%20amber%20and%20teal%20color%20grading%2C%20soft%20rim%20light%2C%20blurred%20atmospheric%20background%2C%20shallow%20depth%20of%20field%2C%20editorial%20photography%2C%20no%20text%2C%20no%20watermark&width=800&height=500&seq=ethilmic-col-02&orientation=landscape",
  },
  {
    id: "c3",
    name: "高分悬疑伦理合集",
    count: 21,
    desc: "真相往往不止一个版本。这批条目把悬疑的钩子放在伦理的裂缝里，越往后看越舍不得停。",
    highlights: ["多重反转", "家庭秘密", "心理博弈"],
    cover: "https://readdy.ai/api/search-image?query=a%20moody%20empty%20room%20with%20a%20single%20window%20and%20long%20shadows%20across%20the%20floor%20cinematic%20film%20still%2C%20warm%20amber%20and%20teal%20color%20grading%2C%20soft%20rim%20light%2C%20blurred%20atmospheric%20background%2C%20shallow%20depth%20of%20field%2C%20editorial%20photography%2C%20no%20text%2C%20no%20watermark&width=800&height=500&seq=ethilmic-col-03&orientation=landscape",
  },
  {
    id: "c4",
    name: "文艺与人性合集",
    count: 24,
    desc: "节奏慢、留白多，但后劲很足。适合愿意花两个安静小时、和自己对对话的观众。",
    highlights: ["四季与自然意象", "孤独与救赎", "诗意长镜头"],
    cover: "https://readdy.ai/api/search-image?query=a%20vast%20misty%20landscape%20with%20a%20lone%20tree%20and%20soft%20gradient%20sky%20at%20dawn%20cinematic%20film%20still%2C%20warm%20amber%20and%20teal%20color%20grading%2C%20soft%20rim%20light%2C%20blurred%20atmospheric%20background%2C%20shallow%20depth%20of%20field%2C%20editorial%20photography%2C%20no%20text%2C%20no%20watermark&width=800&height=500&seq=ethilmic-col-04&orientation=landscape",
  },
];

export const editorPicks: EditorPick[] = [
  {
    filmId: "f09",
    label: "本周最推荐",
    note: "它把一场劳资争议拍成了关于体面的故事。没有刻意煽情，但结尾那群女工走向海边的长镜头，会让人记很久。如果你只打算看一部，从这部开始。",
  },
  {
    filmId: "f05",
    label: "悬疑首选",
    note: "悬疑外壳下其实是一个关于「保护」的伦理命题。它不急着给你答案，而是让你和主角一起在沉默里做选择。喜欢边看边推理的观众不要错过。",
  },
  {
    filmId: "f01",
    label: "深夜静片",
    note: "节奏极慢，但慢得有道理。适合一个人、戴上耳机、把手机放下再看。母女之间那些没说出口的话，几乎每一句都能对上你自己的生活。",
  },
];

export const newArrivals: NewArrival[] = [
  { filmId: "f18", date: "10 月 08 日", subtitle: "中字高清", format: "1080P · 杜比音效" },
  { filmId: "f05", date: "10 月 06 日", subtitle: "中字高清", format: "1080P · 可选日语原声" },
  { filmId: "f12", date: "10 月 04 日", subtitle: "中字高清", format: "720P / 1080P" },
  { filmId: "f15", date: "10 月 02 日", subtitle: "中文字幕", format: "1080P · 北欧取景" },
  { filmId: "f02", date: "09 月 29 日", subtitle: "中字高清", format: "1080P · 加长版" },
  { filmId: "f17", date: "09 月 26 日", subtitle: "中文字幕", format: "1080P · 附导演短片" },
];

export const genres: string[] = ["全部", "家庭伦理", "社会现实", "悬疑伦理", "都市情感", "文艺剧情"];

export const genreIntro: Record<string, string> = {
  全部: "全部题材混排，适合还没想好方向的观众，先随便翻，看到标题有感觉再点开简介。",
  家庭伦理: "围绕血缘与亲密关系的取舍展开，讨论原谅、赡养、代际误解这类每个人都可能遇到的问题。",
  社会现实: "把个人命运放进更大的时代背景里，关注劳动者、迁徙者与普通人的处境与尊严。",
  悬疑伦理: "用悬念牵引观众，但真正的落点仍是伦理选择——当真相与善意冲突时，人该怎么选。",
  都市情感: "发生在城市里的相遇与重逢，节奏相对轻，但不回避孤独与遗憾这些真实的情绪。",
  文艺剧情: "重氛围与意象，节奏偏慢，适合愿意安静看完、在意情绪与画面的观众。",
};

export const guideSteps: GuideStep[] = [
  {
    id: "g1",
    title: "第一步：先确定你今晚的情绪",
    summary: "想被治愈、想被挑战，还是只想安安静静看完？",
    detail: "选片前先问自己一句：我现在想获得什么感受。想放松，就在都市情感或家庭伦理的合集里挑；想被震动、愿意动脑，悬疑伦理与社会现实更合适；如果只是想让情绪慢慢沉下来，文艺剧情的快节奏负担最小。用情绪倒推题材，比盲目刷榜单高效得多。",
  },
  {
    id: "g2",
    title: "第二步：看导演与创作脉络",
    summary: "同一位导演的作品通常有稳定的气质，顺着脉络找不容易踩雷。",
    detail: "本站的条目都会标注导演信息。当你喜欢某一部片的镜头语言和叙事方式时，不妨看看同一位导演的其他作品，往往能保持相近的水准与审美。对于刚接触伦理片的观众，这是风险最低的选片方式之一。",
  },
  {
    id: "g3",
    title: "第三步：参考评分与榜单趋势",
    summary: "评分看基线，趋势看热度，两者结合更接近真实口碑。",
    detail: "综合评分反映的是长期口碑，适合用来排除明显不合格的条目；而榜单里的上升、下降与新上榜标记，则能告诉你最近大家在讨论什么。建议先按评分筛出及格线以上的作品，再结合趋势挑出当下更值得看的那几部。",
  },
  {
    id: "g4",
    title: "第四步：留意字幕与画质信息",
    summary: "中字是否齐全、画质是否高清，直接决定观看体验。",
    detail: "本页在每条推荐里都标注了字幕与画质情况。观看前建议确认是中字高清版本，尤其是对白密集、依赖细节表演的伦理片，字幕质量会明显影响理解。如果遇到只有低清资源的情况，可以先加入待看清单，等待更好的版本。",
  },
];

export const faqs: FaqItem[] = [
  {
    id: "q1",
    question: "伦理片到底是什么类型的电影？",
    answer: "伦理片通常指以人与人之间的道德、情感与责任关系为核心矛盾的剧情电影，常见主题包括家庭、婚姻、代际关系与社会处境。它更关注人在两难中的选择和心理变化，而不是单纯的类型刺激。",
  },
  {
    id: "q2",
    question: "本站的推荐是怎么排序的？",
    answer: "排行榜综合了站内评分、观看量与近期讨论热度，并标注了趋势变化；编辑精选则由编辑根据叙事完成度、表演与主题深度人工挑选，两种维度互补，你可以按偏好各取所需。",
  },
  {
    id: "q3",
    question: "不清楚自己的口味，该怎么开始？",
    answer: "建议先看「观影指南」的四步选片法，再从「家庭伦理精选合集」里挑一部评分在 8.0 以上的作品试看。节奏偏慢、情感表达细腻的片子，通常是这个题材最好的入门选择。",
  },
  {
    id: "q4",
    question: "页面上的内容会更新吗？",
    answer: "会。排行榜按天维护，新片速递与编辑精选会随新条目上架同步调整。目前页面展示的是本期内容，后续更新会沿用相同的分区结构，方便你直接找到熟悉的位置。",
  },
  {
    id: "q5",
    question: "如何在手机上更舒服地浏览？",
    answer: "本页针对移动端做了适配，导航、标签与筛选控件都支持点按与键盘操作。内容卡片会自动堆叠为单列，长简介可以自然换行阅读，建议把字号调到系统默认即可获得较舒适的排版。",
  },
  {
    id: "q6",
    question: "推荐里有我不想看的题材，怎么过滤掉？",
    answer: "在「按题材浏览」分区，你可以通过题材标签切换、地区筛选与关键词搜索快速缩小范围。所有筛选都只作用于本页已展示的内容，不会跳转到其他页面，切换后可以随时点「全部」恢复完整列表。",
  },
];

export const navItems = [
  { id: "ranking", label: "排行榜" },
  { id: "collections", label: "高清合集" },
  { id: "genres", label: "题材浏览" },
  { id: "picks", label: "编辑精选" },
  { id: "new", label: "新片速递" },
  { id: "guide", label: "观影指南" },
  { id: "faq", label: "常见问题" },
];